import { baseApi } from "./baseApi";

const ENTITY_BASE_URL = "/entities";

export const entityApi = baseApi.injectEndpoints({
  tagTypes: ["Entity"],

  endpoints: (build) => ({
    registerEntity: build.mutation({
      query(body) {
        return {
          url: `${ENTITY_BASE_URL}/register`,
          method: "POST",
          body,
        };
      },
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created Entity could show up in any lists.
      invalidatesTags: [{ type: "Entity", id: "LIST" }],
    }),

    addEntity: build.mutation({
      query(body) {
        return {
          url: `${ENTITY_BASE_URL}/add`,
          method: "POST",
          body,
        };
      },
      // Invalidates all Post-type queries providing the `LIST` id - after all, depending of the sort order,
      // that newly created Entity could show up in any lists.
      invalidatesTags: [{ type: "Entity", id: "LIST" }],
    }),

    editEntity: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `${ENTITY_BASE_URL}/edit/${id}`,
          method: "PUT",
          body,
        };
      },
      // Invalidates all queries that subscribe to this Entity `id` only.
      // In this case, `detailEntity` will be re-run. `listEntity` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { _id }) => [
        { type: "Entity", id: _id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    deleteEntity: build.mutation({
      query(id) {
        return {
          url: `${ENTITY_BASE_URL}/delete/${id}`,
          method: "DELETE",
        };
      },
      // Invalidates all queries that subscribe to this Entity `id` only.
      invalidatesTags: (result, error, id) => [
        { type: "Entity", id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    deactivateEntity: build.mutation({
      query(id) {
        return {
          url: `${ENTITY_BASE_URL}/edit/${id}/inactive`,
          method: "PUT",
        };
      },
      // Invalidates all queries that subscribe to this Coa `id` only.
      invalidatesTags: (result, error, id) => [
        { type: "Entity", id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    activateEntity: build.mutation({
      query(id) {
        return {
          url: `${ENTITY_BASE_URL}/edit/${id}/active`,
          method: "PUT",
        };
      },
      // Invalidates all queries that subscribe to this Coa `id` only.
      invalidatesTags: (result, error, id) => [
        { type: "Entity", id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    addUserToEntity: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `${ENTITY_BASE_URL}/${id}/add-user`,
          method: "PUT",
          body,
        };
      },
      // Invalidates all queries that subscribe to this Entity `id` only.
      // In this case, `detailEntity` will be re-run. `listEntity` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [
        { type: "Entity", id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    removeUserFromEntity: build.mutation({
      query(data) {
        const { id, ...body } = data;
        return {
          url: `${ENTITY_BASE_URL}/${id}/remove-user`,
          method: "PUT",
          body,
        };
      },

      // Invalidates all queries that subscribe to this Entity `id` only.
      // In this case, `detailEntity` will be re-run. `listEntity` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { id }) => [
        { type: "Entity", id },
        { type: "Entity", id: "LIST" },
      ],
    }),

    // To get count of entity and licenses
    detailEntityCount: build.query({
      query: () => `${ENTITY_BASE_URL}/count`,
      providesTags: () => [{ type: "EntityCount", id: "LIST" }],
    }),

    //Specific slice for Account-Level Admins
    listAccountEntity: build.query({
      query: (status) => `${ENTITY_BASE_URL}/list/account?status=${status}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Entity", id })),
              { type: "Entity", id: "LIST" },
            ]
          : [{ type: "Entity", id: "LIST" }],
    }),

    detailAccountEntity: build.query({
      query: (id) => `${ENTITY_BASE_URL}/detail/${id}/account`,
      providesTags: (result, error, id) => [{ type: "Entity", id }],
    }),

    //Specific slice for System-Level Admins
    listSystemEntity: build.query({
      query: (status) => `${ENTITY_BASE_URL}/list/system?status=${status}`,
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Entity", id })),
              { type: "Entity", id: "LIST" },
            ]
          : [{ type: "Entity", id: "LIST" }],
    }),

    listSystemAccountEntity: build.query({
      query: (queryParams) => {
        return {
          url: `${ENTITY_BASE_URL}/list/system/account`,
          params: queryParams,
        };
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Entity", id })),
              { type: "Entity", id: "LIST" },
            ]
          : [{ type: "Entity", id: "LIST" }],
    }),

    listEntities: build.query({
      query: (params) => ({ url: `${ENTITY_BASE_URL}`, params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({ type: "Entity", id: _id })),
              { type: "Entity", id: "LIST" },
            ]
          : [{ type: "Entity", id: "LIST" }],
    }),

    detailEntity: build.query({
      query: (id) => `${ENTITY_BASE_URL}/${id}`,
      providesTags: (result, error, id) => [{ type: "Entity", id: id }],
    }),

    listEntityUsers: build.query({
      query: ({ id, ...params }) => ({
        url: `${ENTITY_BASE_URL}/${id}/users`,
        params,
      }),
      providesTags: (result, error, { id }) => [
        { type: "Entity", id },
        { type: "User", id: "LIST" },
      ],
    }),

    getDDLEntityList: build.query({
      query: (params) => ({
        url: `${ENTITY_BASE_URL}/ddl`,
        params,
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Entity", id })),
              { type: "Entity", id: "DDL" },
            ]
          : [{ type: "Entity", id: "DDL" }],
    }),
    generateApiCredential: build.mutation({
      query: (entityId) => ({
        url: `${ENTITY_BASE_URL}/${entityId}/api-credential`,
        method: "PUT",
      }),
      // Invalidates all queries that subscribe to this Entity `id` only.
      // In this case, `detailEntity` will be re-run. `listEntity` *might*  rerun, if this id was under its results.
      invalidatesTags: (result, error, { _id }) => [
        { type: "Entity", id: _id },
        { type: "Entity", id: "LIST" },
      ],
    }),
    createServiceCharge: build.mutation({
      query: ({ entityId, ...body }) => ({
        url: `${ENTITY_BASE_URL}/${entityId}/service-charge`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: "Entity", id: entityId },
      ],
    }),

    updateServiceCharge: build.mutation({
      query: ({ entityId, ...body }) => ({
        url: `${ENTITY_BASE_URL}/${entityId}/service-charge`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: "Entity", id: entityId },
      ],
    }),

    createDocumentationFee: build.mutation({
      query: ({ entityId, ...body }) => ({
        url: `${ENTITY_BASE_URL}/${entityId}/documentation-fee`,
        method: "POST",
        body,
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: "Entity", id: entityId },
      ],
    }),

    updateDocumentationFee: build.mutation({
      query: ({ entityId, ...body }) => ({
        url: `${ENTITY_BASE_URL}/${entityId}/documentation-fee`,
        method: "PUT",
        body,
      }),
      invalidatesTags: (result, error, { entityId }) => [
        { type: "Entity", id: entityId },
      ],
    }),
    getEntityEBillProvider: build.query({
      query: ({ id, ...params }) => ({
        url: `${ENTITY_BASE_URL}/${id}/e-bill-provider`,
        params,
      }),
      providesTags: (result, error, { id }) => [{ type: "Entity", id }],
    }),
  }),
  overrideExisting: false,
});

export const {
  useRegisterEntityMutation,
  useAddEntityMutation,
  useDetailEntityQuery,
  useListEntityUsersQuery,
  useEditEntityMutation,
  useDeleteEntityMutation,
  useDeactivateEntityMutation,
  useActivateEntityMutation,
  useAddUserToEntityMutation,
  useRemoveUserFromEntityMutation,
  useDetailEntityCountQuery,
  useListAccountEntityQuery,
  useDetailAccountEntityQuery,
  useListSystemEntityQuery,
  useListSystemAccountEntityQuery,
  useLazyListSystemAccountEntityQuery,

  useListEntitiesQuery,
  useLazyListEntitiesQuery,
  useGetDDLEntityListQuery,
  useLazyGetDDLEntityListQuery,

  useGenerateApiCredentialMutation,

  useCreateServiceChargeMutation,
  useUpdateServiceChargeMutation,

  useCreateDocumentationFeeMutation,
  useUpdateDocumentationFeeMutation,

  useGetEntityEBillProviderQuery,
} = entityApi;
